import { useEffect, useState, createContext, useContext } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { cn } from "../../lib/utils";
import {
  LayoutDashboard,
  BookOpen,
  List,
  LogOut,
  Menu,
  ChevronRight,
  Home,
  ShoppingBag,
  Mail,
  MessageSquare,
} from "lucide-react";
import { User } from "@supabase/supabase-js";
import { Button } from "../ui/button";

// Tạo context để chia sẻ trạng thái sidebar và hàm toggle
export interface AdminLayoutContextType {
  isMobile: boolean;
  sidebarVisible: boolean;
  toggleSidebar: () => void;
  setSidebarVisible: (visible: boolean) => void;
}

export const AdminLayoutContext = createContext<AdminLayoutContextType | null>(
  null
);

// Hook để các components con sử dụng
export const useAdminLayout = () => {
  const context = useContext(AdminLayoutContext);
  if (!context) {
    throw new Error(
      "useAdminLayout must be used within an AdminLayoutProvider"
    );
  }
  return context;
};

// Custom component cho nút burger menu
export const SidebarToggle = ({ className }: { className?: string }) => {
  const { toggleSidebar } = useAdminLayout();

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={toggleSidebar}
      className={cn("flex-shrink-0 h-8 w-8 p-0", className)}
    >
      <Menu className="h-4 w-4" />
    </Button>
  );
};

const AdminLayout = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Kiểm tra kích thước màn hình khi component mount và khi resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // 1024px là breakpoint của lg
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();

      if (!data.session) {
        navigate("/admin/login");
        return;
      }

      // Kiểm tra quyền admin
      const { data: userData, error } = await supabase
        .from("admin_users")
        .select("*")
        .eq("user_id", data.session.user.id)
        .single();

      if (error || !userData) {
        await supabase.auth.signOut();
        navigate("/admin/login");
        return;
      }

      setUser(data.session.user);
      setLoading(false);
    };

    checkAuth();
  }, [navigate]);

  // Đóng sidebar khi chuyển route trên mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  }, [location.pathname, isMobile]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  // Hàm toggle sidebar
  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  // Giá trị context để truyền xuống cho các components con
  const contextValue: AdminLayoutContextType = {
    isMobile,
    sidebarVisible,
    toggleSidebar,
    setSidebarVisible,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        Đang tải...
      </div>
    );
  }

  const menuItems = [
    {
      name: "Bảng điều khiển",
      path: "/admin/dashboard",
      icon: <LayoutDashboard className="h-5 w-5" />,
    },
    {
      name: "Bài viết",
      path: "/admin/articles",
      icon: <BookOpen className="h-5 w-5" />,
    },
    {
      name: "Danh mục",
      path: "/admin/categories",
      icon: <List className="h-5 w-5" />,
    },
    {
      name: "Ưu đãi mua sắm",
      path: "/admin/deals",
      icon: <ShoppingBag className="h-5 w-5" />,
    },
    {
      name: "Đăng ký Email",
      path: "/admin/subscriptions",
      icon: <Mail className="h-5 w-5" />,
    },
    {
      name: "Tin nhắn liên hệ",
      path: "/admin/messages",
      icon: <MessageSquare className="h-5 w-5" />,
    },
  ];

  // Kiểm tra xem đường dẫn hiện tại có bắt đầu bằng item.path hay không
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <AdminLayoutContext.Provider value={contextValue}>
      <div className="min-h-screen flex bg-gray-100">
        {/* Backdrop for mobile */}
        {isMobile && sidebarVisible && (
          <div
            className="fixed inset-0 bg-black/20 z-30"
            onClick={() => setSidebarVisible(false)}
          />
        )}

        {/* Sidebar */}
        <div
          className={cn(
            "fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:z-30",
            sidebarVisible ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center space-x-2">
                <LayoutDashboard className="h-6 w-6" />
                <span className="font-semibold text-lg">Admin Portal</span>
              </div>
            </div>

            <div className="flex-grow py-6 px-4 overflow-y-auto">
              <div className="space-y-1">
                {menuItems.map((item) => (
                  <Button
                    key={item.path}
                    variant={isActive(item.path) ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-start text-left font-normal mb-1",
                      isActive(item.path) ? "bg-gray-100 font-medium" : ""
                    )}
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) setSidebarVisible(false);
                    }}
                  >
                    <div className="flex items-center">
                      {item.icon}
                      <span className="ml-3">{item.name}</span>
                      {isActive(item.path) && (
                        <ChevronRight className="ml-auto h-4 w-4" />
                      )}
                    </div>
                  </Button>
                ))}
              </div>
            </div>

            <div className="p-4 border-t">
              <div className="mb-2 text-sm text-gray-500 truncate">
                {user?.email}
              </div>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleSignOut}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Đăng xuất
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start mt-2"
                onClick={() => navigate("/")}
              >
                <Home className="h-4 w-4 mr-2" />
                Về trang chủ
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div
          className={cn(
            "flex-1 transition-all duration-300 ease-in-out overflow-hidden",
            !isMobile && "lg:ml-64"
          )}
        >
          {/* Fixed content container that properly fills the available space */}
          <div className="p-4 md:p-6 w-full overflow-auto">
            <div className="w-full">
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </AdminLayoutContext.Provider>
  );
};

export default AdminLayout;
